<!--
@component
The marketing redesign's only button. Icons are children, placed wherever you
write them:

```svelte
<Button><ProductIcon name="inference" class="size-6" data-icon="inline-start" /> Inference</Button>
<Button>Read the docs <Chevron /></Button>
<Button iconOnly aria-label="Next"><Chevron /></Button>
```

An `<svg>` child is sized by the size variant unless it has its own `size-*`
class, and inherits `currentColor`. `iconOnly` switches to square padding.
Tag a trailing chevron `data-icon="inline-end"` so the tertiary variant dims it.

There is no theme prop: each variant follows the nearest `data-v2-theme`
ancestor, including nested dark islands inside a light band.

Renders an `<a>` when `href` is set, otherwise a `<button>`. A disabled link
drops its `href`, keeps `role="link"` and leaves the tab order.
-->

<script module lang="ts">
  import type { Snippet } from "svelte";
  import type {
    ClassValue,
    HTMLAnchorAttributes,
    HTMLButtonAttributes,
  } from "svelte/elements";

  import type {
    ButtonSize as Size,
    ButtonVariant as Variant,
  } from "./variants";

  export type { ButtonSize, ButtonVariant } from "./variants";

  export type ButtonProps = {
    /** Emphasis level. */
    variant?: Variant;
    /** Padding step, and the auto-size applied to icon children. */
    size?: Size;
    /** Square padding for a button whose only child is an icon. Needs an aria-label. */
    iconOnly?: boolean;
    /** When set, renders an anchor instead of a button. */
    href?: string;
    /** Dims the button and blocks activation. */
    disabled?: boolean;
    /** Opens in a new tab and adds rel="noreferrer". */
    newTab?: boolean;
    /** Adds rel="external" to the link. */
    external?: boolean;
    onclick?: (event: MouseEvent) => void;
    class?: ClassValue;
    children?: Snippet;
  } & Omit<
    HTMLAnchorAttributes & HTMLButtonAttributes,
    "href" | "disabled" | "onclick" | "class"
  >;
</script>

<script lang="ts">
  import { base, iconSizes, sizes, tertiarySizes, variants } from "./variants";

  let {
    variant = "secondary",
    size = "sm",
    iconOnly = false,
    href,
    disabled = false,
    newTab = false,
    external = false,
    onclick,
    class: className,
    children,
    ...rest
  }: ButtonProps = $props();

  const spacing = $derived(
    iconOnly
      ? iconSizes[size]
      : variant === "tertiary"
        ? tertiarySizes[size]
        : sizes[size],
  );

  const classes = $derived([
    base,
    variants[variant],
    spacing,
    className,
  ] as ClassValue);

  const rel = $derived.by(() => {
    const values: string[] = [];
    if (newTab) values.push("noreferrer");
    if (external) values.push("external");
    return values.length > 0 ? values.join(" ") : undefined;
  });
</script>

{#if href}
  <a
    data-slot="button"
    data-disabled={disabled || undefined}
    class={classes}
    href={disabled ? undefined : href}
    role={disabled ? "link" : undefined}
    aria-disabled={disabled || undefined}
    tabindex={disabled ? -1 : undefined}
    target={newTab ? "_blank" : undefined}
    {rel}
    onclick={disabled ? undefined : onclick}
    {...rest}
  >
    {@render children?.()}
  </a>
{:else}
  <button
    data-slot="button"
    data-disabled={disabled || undefined}
    class={classes}
    type="button"
    {disabled}
    {onclick}
    {...rest}
  >
    {@render children?.()}
  </button>
{/if}
